import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Icon from "components/AppIcon";

import { useNavigation } from "components/ui/NavigationStateManager";
import ProductImageGallery from "./components/ProductImageGallery";
import ProductInfo from "./components/ProductInfo";
import ProductVariants from "./components/ProductVariants";
import { apiHandler } from "api";
import { endpoint } from "../../api/endpoint";

const ProductDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentStore, getDisplayStoreName } = useNavigation();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariantProduct, setSelectedVariantProduct] = useState(null);
  const [variantProducts, setVariantProducts] = useState(null);
  const [loading, setLoading] = useState(true);

  const getProductsVariants = async () => {
    try {
      setLoading(true);
      const response = await apiHandler({
        url: `${endpoint.GET_PRODUCTS_VARIANTS_BY_ID}?iVariantId=${selectedProduct?.iVariantId}`,
        method: "GET",
        tenantId: currentStore?.iTenantId,
      });
      if (response?.data?.data?.variants) {
        setVariantProducts(response?.data?.data?.variants);
        setSelectedVariantProduct(response?.data?.data?.variants[0]);
      }
    } catch (error) {
      console.log("Error fetching products ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProduct?.iVariantId && selectedProduct?.iVariantCount > 1) {
      getProductsVariants();
    } else {
      setVariantProducts([selectedProduct]);
      setSelectedVariantProduct(selectedProduct);
    }
  }, [selectedProduct]);

  useEffect(() => {
    const productData = sessionStorage.getItem("selectedProduct");
    if (productData) {
      try {
        const product = JSON.parse(productData);
        setSelectedProduct(product);
      } catch (error) {
        console.error("Error parsing product data:", error);
      }
    }
    setLoading(false);
  }, [location]);

  useEffect(() => {
    if (!currentStore) {
      navigate("/HomePage");
    }
  }, [currentStore, navigate]);

  const handleBackToListing = () => {
    navigate("/product-listing-page");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container-app section-spacing">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading product details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedVariantProduct) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container-app section-spacing">
          <div className="text-center py-16">
            <Icon
              name="AlertCircle"
              size={48}
              className="text-error mx-auto mb-4"
            />
            <h2 className="text-heading-2 mb-4">Product Not Found</h2>
            <p className="text-text-secondary mb-8">
              The product you're looking for doesn't exist or has been removed.
            </p>
            <button onClick={handleBackToListing} className="btn-primary">
              <Icon name="ArrowLeft" size={20} />
              Back to Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const image = (() => {
    try {
      if (!selectedVariantProduct) return "";
      let images;
      if (typeof selectedVariantProduct.aImageJson === "string") {
        images = JSON.parse(selectedVariantProduct.aImageJson);
      } else if (Array.isArray(selectedVariantProduct.aImageJson)) {
        images = selectedVariantProduct.aImageJson;
      } else {
        return "";
      }
      if (!Array.isArray(images)) return "";
      const frontImage = images.find((img) => img?.sTag === "frontImage");
      const frontFullImage = images.find(
        (img) => img?.sTag === "frontFullImage"
      );
      return (
        frontImage?.sURL || frontFullImage?.sURL || images?.[0]?.sURL || ""
      );
    } catch (error) {
      console.warn("Error extracting image URL:", error);
      return "";
    }
  })();

  return (
    <div className="min-h-screen bg-background">
      <div className="container-app section-spacing">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToListing}
                className="btn-ghost flex items-center space-x-2"
              >
                <Icon name="ArrowLeft" size={20} />
                <span>Back to Products</span>
              </button>

              <div className="hidden md:flex items-center space-x-2 text-sm text-text-secondary">
                <Icon name="Store" size={16} />
                <span>Demo in:</span>
                <span className="font-medium text-text-primary">
                  {getDisplayStoreName()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-6">
            <ProductImageGallery
              images={image}
              productName={selectedVariantProduct?.sProductName}
            />
          </div>
          <div className="lg:col-span-6">
            <ProductInfo
              currentStore={currentStore}
              product={selectedVariantProduct}
            />
            {variantProducts.length > 1 ? (
              <div className="mt-8">
                <ProductVariants
                  variantProducts={variantProducts}
                  setSelectedVariantProduct={setSelectedVariantProduct}
                  selectedVariantProduct={selectedVariantProduct}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
